import { 
  Breadcrumb as UIBreadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '@/components/ui/breadcrumb'
import { useLocation, Link } from 'react-router-dom'
import { Home } from 'lucide-react'

interface BreadcrumbProps {
  items?: Array<{
    label: string
    href?: string
  }>
}

const Breadcrumb = ({ items }: BreadcrumbProps) => {
  const location = useLocation()
  
  // Generate breadcrumb items from current path if not provided
  const generateItems = () => {
    if (items) return items
    
    const pathSegments = location.pathname.split('/').filter(Boolean)
    const breadcrumbItems = []
    
    // Map path segments to German labels
    const pathLabels: { [key: string]: string } = {
      'admin': 'Verwaltung',
      'kunde': 'Kunde', 
      'superadmin': 'System',
      'customers': 'Unterstützer',
      'unterstützer': 'Unterstützer',
      'unterstuetzer': 'Unterstützer',
      'belohnungen': 'Prämien',
      'rewards': 'Prämien',
      'verlauf': 'Verlauf',
      'history': 'Verlauf',
      'profil': 'Profil',
      'profile': 'Profil',
      'empfehlungen': 'Empfehlungen',
      'referrals': 'Empfehlungen',
      'bestenliste': 'Bestenliste',
      'leaderboard': 'Bestenliste',
      'einstellungen': 'Einstellungen',
      'settings': 'Einstellungen',
      'stempel': 'Stempel',
      'stamps': 'Stempel',
      'qr-codes': 'QR-Codes',
      'analytics': 'Analytik',
      'analytik': 'Analytik',
      'referral-crm': 'Empfehlungs-CRM',
      'landing-pages': 'Landing Pages',
      'builder': 'Builder',
      'unternehmen': 'Unternehmen',
      'benutzer': 'Benutzer',
      'crm': 'CRM'
    }
    
    // Add first level (Dashboard/Übersicht)
    if (pathSegments.length > 0) {
      const firstSegment = pathSegments[0]
      let dashboardLabel = 'Übersicht'
      
      if (firstSegment === 'admin') dashboardLabel = 'Verwaltung'
      else if (firstSegment === 'superadmin') dashboardLabel = 'System'
      else if (firstSegment === 'kunde') dashboardLabel = 'Kunde'
      
      breadcrumbItems.push({ label: dashboardLabel, href: `/${firstSegment}` })
    }
    
    // Add path segments
    for (let i = 1; i < pathSegments.length; i++) {
      const segment = pathSegments[i]
      const href = `/${pathSegments.slice(0, i + 1).join('/')}`
      
      // Get German label or default to capitalized segment
      // Handle URL encoded segments
      const decodedSegment = decodeURIComponent(segment)
      const label = pathLabels[decodedSegment] || pathLabels[segment] || decodedSegment.charAt(0).toUpperCase() + decodedSegment.slice(1)
      
      breadcrumbItems.push({ 
        label, 
        href: i === pathSegments.length - 1 ? undefined : href 
      })
    }
    
    return breadcrumbItems
  }

  const breadcrumbItems = generateItems()

  return (
    <UIBreadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/" className="flex items-center gap-1">
              <Home className="h-3 w-3" />
              Startseite
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        
        {breadcrumbItems.map((item, index) => (
          <div key={index} className="flex items-center">
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              {item.href ? (
                <BreadcrumbLink asChild>
                  <Link to={item.href}>{item.label}</Link>
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage>{item.label}</BreadcrumbPage>
              )}
            </BreadcrumbItem>
          </div>
        ))}
      </BreadcrumbList>
    </UIBreadcrumb>
  )
}

export default Breadcrumb